import { useEffect, useState } from "react";
import { adminApi, formatAud } from "../../lib/api";
import { AlertTriangle, CheckCircle2, RefreshCcw, X, ShoppingBag, FileText, CreditCard, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const SEVERITY_STYLE = {
  high:   { bg:"#fee2e2", color:"#991b1b", border:"1px solid #fca5a5", dot:"#e31e24" },
  medium: { bg:"#fffbeb", color:"#b45309", border:"1px solid #fcd34d", dot:"#d97706" },
  low:    { bg:"#dbeafe", color:"#1e40af", border:"1px solid #93c5fd", dot:"#1e5bb8" },
};

const TYPE_LABEL = {
  negative_stock:  "Negative Stock",
  double_sale:     "Double Sale",
  stock_mismatch:  "Stock Mismatch",
  order_duplicate: "Duplicate Order",
  price_deviation: "Price Deviation",
  return_conflict: "Return Conflict",
};

const REF_ICON = { order:ShoppingBag, product:FileText, credit:CreditCard, return:RotateCcw };
const REF_STYLE = {
  order:   { bg:"#dbeafe", color:"#1e40af" },
  product: { bg:"#dcfce7", color:"#15803d" },
  credit:  { bg:"#ede9fe", color:"#5b21b6" },
  return:  { bg:"#fef3c7", color:"#92400e" },
};

export default function AdminConflicts() {
  const [items,        setItems]        = useState([]);
  const [stats,        setStats]        = useState({ open:0, high_severity:0 });
  const [statusFilter, setStatusFilter] = useState("open");
  const [resolving,    setResolving]    = useState(null);
  const [resolution,   setResolution]   = useState("");

  const load = async () => {
    try {
      const { data } = await adminApi.get(`/admin/conflicts?status=${statusFilter}&limit=200`);
      setItems(data.items || []);
      setStats(data.stats || { open:0, high_severity:0 });
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const resolve = async (id) => {
    if (!resolution.trim()) { toast.error("Enter a resolution note"); return; }
    try {
      await adminApi.post(`/admin/conflicts/${id}/resolve`, { resolution });
      toast.success("Conflict resolved");
      setResolving(null); setResolution(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const ignore = async (id) => {
    if (!window.confirm("Mark this conflict as ignored? It will be dismissed without action.")) return;
    try {
      await adminApi.post(`/admin/conflicts/${id}/ignore`);
      toast.success("Conflict ignored"); load();
    } catch { toast.error("Failed"); }
  };

  const highCount = items.filter(i=>i.severity==="high").length;

  return (
    <div className="space-y-5" data-testid="admin-conflicts-page">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color:"#94a3b8" }}>Sync Safety</div>
          <h1 className="font-heading text-[28px] font-bold" style={{ color:"#0f172a" }}>Data Conflicts</h1>
          <p className="text-[12px] mt-0.5" style={{ color:"#64748b" }}>
            Discrepancies between BCP and Pinnacle requiring manual review
          </p>
        </div>
        <button onClick={load}
          className="flex items-center gap-2 px-4 h-9 rounded-xl text-[12px] font-semibold"
          style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}>
          <RefreshCcw className="h-3.5 w-3.5"/> Refresh
        </button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label:"Open conflicts",   value: stats.open,          accent:"#e31e24" },
          { label:"High severity",    value: stats.high_severity, accent:"#991b1b" },
          { label:"Resolved",         value: items.filter(i=>i.status==="resolved").length, accent:"#16a34a" },
        ].map(({ label, value, accent }) => (
          <div key={label} className="bg-white rounded-2xl p-4 shadow-sm"
            style={{ border:"1px solid #dde3ef", borderTop:`3px solid ${accent}` }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color:"#94a3b8" }}>{label}</div>
            <div className="font-heading text-[26px] font-bold" style={{ color:"#0f172a" }}>{value}</div>
          </div>
        ))}
      </div>

      {/* High severity alert */}
      {highCount > 0 && statusFilter === "open" && (
        <div className="flex items-start gap-3 p-4 rounded-2xl"
          style={{ background:"#fee2e2", border:"1px solid #fca5a5" }}>
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" style={{ color:"#e31e24" }}/>
          <div>
            <div className="font-heading font-bold text-[14px] uppercase" style={{ color:"#991b1b" }}>
              {highCount} high-severity conflict{highCount!==1?"s":""} require immediate action
            </div>
            <p className="text-[12px] mt-0.5" style={{ color:"#7f1d1d" }}>
              These may indicate stock discrepancies or duplicate orders. Resolve before next Pinnacle sync.
            </p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-1.5">
        {["open","resolved","ignored"].map(s=>(
          <button key={s} onClick={()=>setStatusFilter(s)}
            className="h-8 px-4 rounded-xl text-[11px] font-mono uppercase tracking-widest transition-all"
            style={statusFilter===s
              ? { background:"#0f172a", color:"#ffffff" }
              : { background:"#f5f7fb", border:"1px solid #dde3ef", color:"#64748b" }
            }>
            {s}
          </button>
        ))}
      </div>

      {/* Conflicts list */}
      <div className="space-y-3">
        {items.map((c) => {
          const sev  = SEVERITY_STYLE[c.severity] || SEVERITY_STYLE.low;
          const Icon = REF_ICON[c.ref_type] || FileText;
          const ref  = REF_STYLE[c.ref_type] || { bg:"#f5f7fb", color:"#475569" };
          return (
            <div key={c.id} className="bg-white rounded-2xl shadow-sm"
              style={{ border:"1px solid #dde3ef" }}
              data-testid={`conflict-${c.id}`}>

              {/* Conflict header */}
              <div className="p-4 flex items-start gap-4 flex-wrap">
                {/* Severity dot */}
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background:sev.bg, border:sev.border }}>
                  <AlertTriangle className="h-5 w-5" style={{ color:sev.dot }}/>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <div className="font-heading font-bold text-[15px]" style={{ color:"#0f172a" }}>
                      {TYPE_LABEL[c.type] || c.type}
                    </div>
                    <span className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-lg"
                      style={{ background:sev.bg, color:sev.color, border:sev.border }}>
                      {c.severity}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-lg"
                      style={{ background:ref.bg, color:ref.color }}>
                      <Icon className="h-3 w-3"/> {c.ref_type}
                    </span>
                  </div>
                  <div className="text-[13px]" style={{ color:"#475569" }}>{c.description}</div>
                  <div className="flex items-center gap-3 mt-1 text-[11px] font-mono" style={{ color:"#94a3b8" }}>
                    <span>{new Date(c.detected_at).toLocaleString("en-AU")}</span>
                  </div>

                  {c.enriched && (
                    <div className="mt-3 space-y-2">
                      {c.enriched.product && (
                        <div className="flex items-center gap-2 text-[12px] p-2 rounded-lg" style={{ background:"#f8fafc" }}>
                          <FileText className="h-3.5 w-3.5 shrink-0" style={{ color:"#64748b" }} />
                          <span className="font-semibold" style={{ color:"#0f172a" }}>{c.enriched.product.name}</span>
                          <span style={{ color:"#94a3b8" }}>
                            {[c.enriched.product.vehicle_year, c.enriched.product.vehicle_make, c.enriched.product.vehicle_model].filter(Boolean).join(" ")}
                            {c.enriched.product.sku ? ` · SKU ${c.enriched.product.sku}` : ""}
                            {typeof c.enriched.product.stock === "number" ? ` · ${c.enriched.product.stock} in stock` : ""}
                          </span>
                        </div>
                      )}
                      {(c.enriched.order_a || c.enriched.order_b) && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {[c.enriched.order_a, c.enriched.order_b].filter(Boolean).map((o, i) => (
                            <div key={i} className="p-2.5 rounded-lg text-[12px]" style={{ background:"#f8fafc", border:"1px solid #eef2f8" }}>
                              <div className="flex items-center justify-between">
                                <span className="font-semibold" style={{ color:"#0f172a" }}>{o.order_number || "—"}</span>
                                {o.source && (
                                  <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-md" style={{ background:"#eef2f8", color:"#64748b" }}>
                                    {o.source}
                                  </span>
                                )}
                              </div>
                              <div style={{ color:"#64748b" }}>{o.customer_name || "Unknown customer"}</div>
                              {o.customer_email && <div style={{ color:"#94a3b8" }}>{o.customer_email}</div>}
                              <div className="flex items-center justify-between mt-1">
                                {typeof o.total === "number" && <span className="font-mono font-bold" style={{ color:"#0f172a" }}>{formatAud(o.total)}</span>}
                                {o.payment_status && (
                                  <span className="text-[9px] font-mono uppercase" style={{ color: o.payment_status === "paid" ? "#15803d" : "#94a3b8" }}>
                                    {o.payment_status}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                {c.status === "open" && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => { setResolving(c.id); setResolution(""); }}
                      className="h-8 px-3 rounded-lg text-[11px] font-semibold"
                      style={{ background:"#dcfce7", color:"#15803d", border:"1px solid #86efac" }}
                      data-testid={`conflict-resolve-${c.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 inline mr-1"/> Resolve
                    </button>
                    <button onClick={() => ignore(c.id)}
                      className="h-8 px-3 rounded-lg text-[11px] font-semibold"
                      style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#64748b" }}
                      data-testid={`conflict-ignore-${c.id}`}>
                      Ignore
                    </button>
                  </div>
                )}
                {c.status !== "open" && (
                  <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-lg"
                    style={{ background:"#f1f5f9", color:"#475569", border:"1px solid #e2e8f0" }}>
                    {c.status}
                  </span>
                )}
              </div>

              {/* Resolve form */}
              {resolving === c.id && (
                <div className="px-4 pb-4 pt-0 flex gap-2">
                  <input value={resolution} onChange={e=>setResolution(e.target.value)}
                    placeholder="Describe how this was resolved…"
                    className="flex-1 h-10 px-3 rounded-xl text-[13px] outline-none"
                    style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#0f172a" }}
                    data-testid={`conflict-note-${c.id}`}
                    onKeyDown={e=>{ if(e.key==="Enter") resolve(c.id); }}/>
                  <button onClick={() => resolve(c.id)}
                    className="h-10 px-4 rounded-xl text-white text-[12px] font-bold uppercase tracking-wide"
                    style={{ background:"#16a34a" }}>
                    Save
                  </button>
                  <button onClick={() => setResolving(null)}
                    className="h-10 w-10 rounded-xl flex items-center justify-center"
                    style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#94a3b8" }}>
                    <X className="h-4 w-4"/>
                  </button>
                </div>
              )}

              {/* Resolution note */}
              {c.resolution && (
                <div className="px-4 pb-4">
                  <div className="text-[11px] font-mono uppercase tracking-widest mb-1" style={{ color:"#94a3b8" }}>Resolution</div>
                  <div className="text-[13px]" style={{ color:"#475569" }}>{c.resolution}</div>
                  {c.resolved_by && (
                    <div className="text-[10px] font-mono mt-0.5" style={{ color:"#94a3b8" }}>
                      by {c.resolved_by} · {c.resolved_at && new Date(c.resolved_at).toLocaleString("en-AU")}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {items.length === 0 && (
          <div className="bg-white rounded-2xl p-14 text-center shadow-sm" style={{ border:"1px solid #dde3ef" }}>
            <CheckCircle2 className="h-12 w-12 mx-auto mb-3" style={{ color:"#86efac" }}/>
            <div className="font-heading font-bold text-[17px] mb-1" style={{ color:"#0f172a" }}>
              {statusFilter === "open" ? "No open conflicts" : `No ${statusFilter} conflicts`}
            </div>
            <div className="text-[13px]" style={{ color:"#64748b" }}>
              {statusFilter === "open" ? "All data between BCP and Pinnacle is in sync" : "Nothing to show here"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}